// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { traerNomina } from "../services/rrhh";
import {
  comoSeLeeLaNovedad,
  comoSemaforoNomina,
  semaforoPersona,
  vigentesHoy,
} from "../services/rrhhNovedades";
import {
  traerCalificaciones,
  setCalificacion,
  quitarCalificacion,
  NIVELES,
  dotDe,
  rotuloCalif,
} from "../services/calificacion";
import { etiquetaCorta, claseDeEmpresa } from "../shared/empresa";
import { SECTORES, porSector, queHayEnOtros, sectorDe } from "../shared/nomina";
import { claveDni } from "../shared/nominaSemaforo";
import { rutaDePersona } from "../shared/personas";
import { antiguedad } from "../shared/rrhh";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* La nómina de sueldos: los 292 que cobran este mes.
 *
 * ── ES LA MISMA TABLA QUE «CHOFERES», Y NO ES LA MISMA PANTALLA ──────────────
 *
 * `/choferes` son los 170 que cobran manejando, vive en Padrones y la abre
 * cualquiera con `ver.gente`: nombre, empresa y base, que es lo que necesita
 * tráfico para armar un viaje.
 *
 * Ésta son las 292 personas y pide `ver.rrhh`, que hoy tienen dos: Vilte y
 * Erick. Lo que agrega es lo que no está en ningún otro lado del hub — la
 * antigüedad, el encuadre del convenio, el tipo de contrato y, cuando lleguen,
 * los motivos de egreso.
 *
 * ── LOS QUE YA NO TRABAJAN SE MUESTRAN APARTE Y NO SE ESCONDEN ───────────────
 *
 * Igna: *«que aparezcan para ser usados únicamente la nómina activa; los
 * inactivos que no estén disponibles —serían ruido— pero que estén en el
 * listado general de personal»*.
 *
 * Acá eso se cumple con un filtro que arranca en «Activos» y no borrando las
 * filas: ésta ES la pantalla del listado general de RRHH, y una baja que no
 * figura en ninguna parte es una baja que no se puede consultar el día que
 * alguien pregunte por qué se fue.
 *
 * Hoy el filtro no cambia nada —los cuatro archivos que mandó Erick son de
 * activos, así que las 292 trabajan— y existe igual porque el día que llegue un
 * export con bajas, la pantalla ya sabe qué hacer con ellas.
 *
 * ── NO SE EDITA ─────────────────────────────────────────────────────────────
 *
 * Mismo motivo que en `/choferes`: el origen es el archivo de Erick y una
 * corrección acá duraría hasta la próxima importación. Lo que esté mal se
 * arregla en la nómina y vuelve a entrar.
 */

const texto = (v) => String(v ?? "").trim();

const ThOrden = ({ columna, orden, alTocar, children }) => {
  const activo = orden.columna === columna;
  return (
    <th>
      <button
        type="button"
        className={"th-orden" + (activo ? " activo" : "")}
        onClick={() => alTocar(columna)}
      >
        {children}
        <span className="th-flecha">
          {!activo ? "" : orden.desc ? " ↓" : " ↑"}
        </span>
      </button>
    </th>
  );
};

/* La fecha como se lee acá: 12/05/2025. */
const fecha = (v) => {
  const s = texto(v).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

/* DOS FILTROS QUE SE CRUZAN, no uno solo con todo mezclado.
 *
 * Antes «Choferes» convivía con «Activos» y «Bajas» en la misma fila, como si
 * fueran la misma pregunta. No lo son: una es EN QUÉ ESTÁ la persona y la otra
 * QUÉ HACE. Puestas juntas no se podía pedir «los administrativos activos»,
 * que es exactamente lo que Nico fue a buscar y no encontró (P-147). */
const FILTROS = [
  { id: "activos", label: "Activos" },
  { id: "bajas", label: "Bajas" },
  { id: "todos", label: "Todos" },
];

const Nomina = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);

  const [filas, setFilas] = useState(undefined); // undefined = cargando
  const [error, setError] = useState(false);
  const [buscar, setBuscar] = useState("");
  const [sector, setSector] = useState("");
  const [filtro, setFiltro] = useState("activos");
  const [orden, setOrden] = useState({ columna: "apellido", desc: false });
  /* Las novedades vigentes hoy, indexadas por DNI, para el círculo amarillo.
     Se leen aparte y su error NO voltea la nómina: sin ellas la lista se
     muestra igual y lo que se pierde es el amarillo — pero se avisa, porque un
     semáforo que mira de menos y se calla muestra verdes que nadie verificó. */
  const [novedades, setNovedades] = useState({});
  const [errorNovedades, setErrorNovedades] = useState("");
  /* P-171: la calificación que carga RRHH (bueno/regular/malo), indexada por
     DNI. Se lee aparte; su error no voltea la nómina —sin ella no se ven los
     puntitos y nada más—. La edita quien tiene `cargar.rrhh`. */
  const [califs, setCalifs] = useState({});
  const puedeCalificar = puede("cargar.rrhh");

  /* SE PIDE SÓLO CUANDO YA SE SABE QUE SE PUEDE, y por eso espera a que los
   * permisos lleguen en vez de asumir que sí como hace el resto del hub. La
   * pared es la policy y no esto —sin `ver.rrhh` la consulta vuelve vacía—
   * pero acá viajan antigüedad, encuadre y motivos de egreso: no hay ninguna
   * razón para pedirlos y confiar en que del otro lado los frenen. */
  const permitido = permisos !== null && permisos.includes("ver.rrhh");

  useEffect(() => {
    if (!permitido) return undefined;
    let vigente = true;
    traerNomina()
      .then((d) => vigente && setFilas(d))
      .catch(() => vigente && setError(true));
    vigentesHoy()
      .then((r) => {
        if (!vigente) return;
        setNovedades(r.porDni || {});
        setErrorNovedades(r.error || "");
      })
      .catch((e) =>
        vigente && setErrorNovedades(e?.message || "No se pudieron leer."),
      );
    traerCalificaciones()
      .then((c) => vigente && setCalifs(c))
      .catch(() => vigente && setCalifs({}));
    return () => {
      vigente = false;
    };
  }, [permitido]);

  /* Calificar o descalificar a una persona. Optimista: se pinta el puntito ya
     y, si la base rechaza, se vuelve atrás y se avisa —así no queda un color
     que en realidad no se guardó—. */
  const calificar = async (dni, nota) => {
    const clave = String(dni);
    const antes = califs[clave];
    setCalifs((c) => {
      const n = { ...c };
      if (nota) n[clave] = { ...(n[clave] || {}), dni: clave, nota };
      else delete n[clave];
      return n;
    });
    try {
      if (nota) await setCalificacion(clave, nota);
      else await quitarCalificacion(clave);
    } catch (e) {
      setCalifs((c) => {
        const n = { ...c };
        if (antes) n[clave] = antes;
        else delete n[clave];
        return n;
      });
      alert("No se pudo guardar la calificación. Probá de nuevo.");
    }
  };

  const alTocar = (columna) =>
    setOrden((o) =>
      o.columna === columna
        ? { columna, desc: !o.desc }
        : { columna, desc: false },
    );

  const cuentas = useMemo(() => {
    const t = filas || [];
    return {
      todos: t.length,
      activos: t.filter((f) => f.trabaja).length,
      bajas: t.filter((f) => !f.trabaja).length,
    };
  }, [filas]);

  /* Cuántos hay en cada sector. Sólo los que trabajan: una baja de hace tres
     años no es «un administrativo», y mezclarla infla el número que alguien va
     a usar para decidir. */
  const porSectorCuenta = useMemo(() => porSector(filas || []), [filas]);

  /* Qué categorías cayeron en «Otros», para poder nombrarlas. El chip no puede
     decir «Otros 4» y dejar a alguien adivinando de qué cuatro habla. */
  const enOtros = useMemo(() => queHayEnOtros(filas || []), [filas]);

  const lista = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    const base = (filas || [])
      .filter((f) => {
        if (filtro === "activos") return f.trabaja;
        if (filtro === "bajas") return !f.trabaja;
        return true;
      })
      .filter((f) => (sector ? sectorDe(f) === sector : true))
      .filter((f) =>
        !q
          ? true
          : [
              f.apellido,
              f.nombres,
              f.dni,
              f.legajo,
              f.destino,
              f.categoria,
              f.calificacion,
            ]
              .map((v) => texto(v).toLowerCase())
              .some((v) => v.includes(q)),
      );
    const signo = orden.desc ? -1 : 1;
    return [...base].sort(
      (a, b) =>
        signo *
        texto(a[orden.columna]).localeCompare(texto(b[orden.columna]), "es", {
          numeric: true,
        }),
    );
  }, [filas, buscar, filtro, sector, orden]);

  /* El permiso se dice, con la salida al lado: «no tengo la llave» y «esto
   * está roto» se ven igual en una pantalla vacía. */
  if (!puede("ver.rrhh")) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <>
      <div className="tk-filtros">
        <input
          type="search"
          placeholder="Buscar por nombre, DNI, legajo, base o categoría"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          aria-label="Buscar en la nómina"
        />
        {FILTROS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={"tk-chip" + (filtro === f.id ? " activo" : "")}
            onClick={() => setFiltro(f.id)}
          >
            {f.label}
            {filas !== undefined && ` ${cuentas[f.id]}`}
          </button>
        ))}
        {/* El total se dice siempre. Una lista filtrada que no aclara cuántos
            hay en total se lee como si ésos fueran todos. */}
        <span className="tk-chip">
          {filas === undefined
            ? "cargando…"
            : `${lista.length} de ${filas.length}`}
        </span>
      </div>

      {/* QUÉ HACE CADA UNO, que es la otra pregunta.
          Los 292 no son 292 choferes: son 171 choferes y 121 personas más que
          hasta ahora sólo aparecían dentro de «Todos», mezcladas. */}
      <div className="tk-filtros tk-filtros--sub">
        <button
          type="button"
          className={"tk-chip" + (sector === "" ? " activo" : "")}
          onClick={() => setSector("")}
        >
          Todos los sectores
        </button>
        {SECTORES.map((x) => {
          const cuantos = porSectorCuenta[x.id] ?? 0;
          /* «Otros» sólo aparece cuando hay alguien adentro. Un chip en cero es
             una pregunta que nadie tiene que hacerse. */
          if (x.id === "otros" && cuantos === 0) return null;
          return (
            <button
              key={x.id}
              type="button"
              className={"tk-chip" + (sector === x.id ? " activo" : "")}
              onClick={() => setSector(sector === x.id ? "" : x.id)}
            >
              {x.label}
              {filas !== undefined && ` ${cuantos}`}
            </button>
          );
        })}
      </div>

      {/* Si hay gente sin clasificar, se la NOMBRA. El sector se deduce de dos
          columnas que escribe otro sistema: una combinación nueva tiene que
          verse, no repartirse en la que más se le parece. */}
      {sector === "otros" && enOtros.length > 0 && (
        <p className="sub">
          Sin clasificar: <b>{enOtros.join(", ")}</b>. Si alguna tendría que ir
          a un sector, avisá y la sumo.
        </p>
      )}

      {errorNovedades && (
        <p className="aduana-error">No se pudo consultar.</p>
      )}

      {/* Vacío NO es lo mismo que "todavía no cargó". Si la nómina nunca se
          importó la tabla está en cero, y decir «no hay nadie» mandaría a
          buscar el error en el lado equivocado. */}
      {filas !== undefined && filas.length === 0 && (
        <div className="panel-aviso suave">
          <h3>Todavía no se importó ninguna nómina</h3>
        </div>
      )}

      {filas !== undefined && filas.length > 0 && lista.length === 0 && (
        <div className="panel-aviso suave">
          <h3>Nada con ese filtro</h3>
        </div>
      )}

      <div className="tabla-wrap">
        <table className="tabla fija">
          <thead>
            <tr>
              <ThOrden columna="apellido" orden={orden} alTocar={alTocar}>
                Persona
              </ThOrden>
              <ThOrden columna="dni" orden={orden} alTocar={alTocar}>
                DNI
              </ThOrden>
              <ThOrden columna="legajo" orden={orden} alTocar={alTocar}>
                Legajo
              </ThOrden>
              <ThOrden columna="empresa" orden={orden} alTocar={alTocar}>
                Empresa
              </ThOrden>
              <ThOrden
                columna="ingreso_computable"
                orden={orden}
                alTocar={alTocar}
              >
                Ingreso
              </ThOrden>
              <ThOrden columna="calificacion" orden={orden} alTocar={alTocar}>
                Tarea
              </ThOrden>
              <ThOrden columna="destino" orden={orden} alTocar={alTocar}>
                Base
              </ThOrden>
            </tr>
          </thead>
          <tbody>
            {lista.map((f) => {
              const ant = antiguedad(f.ingreso_computable, { corto: true });
              const suyas = novedades[claveDni(f.dni)] || [];
              const color = semaforoPersona(f, suyas);
              const bandera = comoSemaforoNomina(color);
              const porQue = suyas.map(comoSeLeeLaNovedad).join(" · ");
              return (
                <tr key={f.dni}>
                  <td>
                    {/* EL CÍRCULO VA ANTES DEL NOMBRE (pedido de Nico): es lo
                        que se barre con la vista bajando la lista, y detrás
                        del nombre quedaría a una distancia distinta en cada
                        fila según cuánto mida el apellido. */}
                    {bandera && (
                      <span
                        className={`nomina-luz nomina-luz--${color}`}
                        title={porQue || bandera.que}
                        aria-label={bandera.label}
                      >
                        {bandera.icono}
                      </span>
                    )}
                    <Link className="rto-link" to={rutaDePersona(f.dni)}>
                      {texto(f.apellido)}, {texto(f.nombres)}
                    </Link>
                    {/* P-171: la calificación de RRHH, DETRÁS del nombre (la luz
                        de novedades va adelante, son cosas distintas). Editable
                        con cargar.rrhh; en sólo-lectura es un puntito con globo.
                        El emoji la separa a ojo de la luz de novedades. */}
                    {(() => {
                      const nota = califs[String(f.dni)]?.nota || "";
                      if (puedeCalificar) {
                        return (
                          <select
                            className="calif-select"
                            value={nota}
                            title={rotuloCalif(nota)}
                            aria-label={`Calificación de ${texto(f.apellido)}`}
                            onChange={(e) => calificar(f.dni, e.target.value)}
                          >
                            <option value="">○ calificar</option>
                            {NIVELES.map((n) => (
                              <option key={n.id} value={n.id}>
                                {n.dot} {n.corto}
                              </option>
                            ))}
                          </select>
                        );
                      }
                      return nota ? (
                        <span
                          className="calif-dot"
                          title={rotuloCalif(nota)}
                          aria-label={rotuloCalif(nota)}
                        >
                          {dotDe(nota)}
                        </span>
                      ) : null;
                    })()}
                    {/* La baja se marca en la fila y no en una columna
                        propia: es lo primero que hay que ver, y una columna
                        más al final se pierde en el scroll horizontal. */}
                    {!f.trabaja && (
                      <span className="pill gris" title={texto(f.motivo_egreso)}>
                        baja{f.egreso ? ` ${fecha(f.egreso)}` : ""}
                      </span>
                    )}
                    {/* «QUE ACLARE POR QUÉ» ES LA MITAD DEL PEDIDO. Un
                        amarillo mudo manda a abrir la ficha para averiguar de
                        qué, y el que abre cinco para encontrar una deja de
                        abrirlas. */}
                    {color === "amarillo" && (
                      <span className="sub nomina-porque">{porQue}</span>
                    )}
                  </td>
                  <td className="dato">{texto(f.dni)}</td>
                  <td className="dato">{texto(f.legajo) || "—"}</td>
                  <td>
                    {/* El CUIT pelado no lo reconoce nadie: la sigla, con el
                        color que ya usa el resto del hub. */}
                    <span className={"pill " + claseDeEmpresa(f.empresa)}>
                      {etiquetaCorta(f.empresa)}
                    </span>
                  </td>
                  <td className="dato">
                    {fecha(f.ingreso_computable) || "—"}
                    {ant && <span className="sub"> · {ant}</span>}
                  </td>
                  <td className="sub">{texto(f.calificacion) || "—"}</td>
                  <td>{texto(f.destino) || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Nomina;
